import { useQuery } from '@tanstack/react-query';
import { StrapiInfoContactoResponse } from '@/types/contact';
import { mapStrapiSeo, ProcessedStrapiSeo } from '@/services/strapi/strapiHomeSeo';

interface UseInfoContactoReturn {
  contactData: StrapiInfoContactoResponse | null;
  seo: ProcessedStrapiSeo | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const fetchInfoContactoData = async (): Promise<StrapiInfoContactoResponse> => {
  const response = await fetch('/api/strapi/info-contacto');
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(`Error HTTP ${response.status}: ${errorData.error || response.statusText}`);
  }
  
  const data: StrapiInfoContactoResponse = await response.json();
  
  // Validar que los datos tengan la estructura esperada
  if (!data || !data.data) {
    throw new Error('Estructura de datos inválida recibida del servidor');
  }
  
  return data;
};

export const useInfoContacto = (): UseInfoContactoReturn => {
  const {
    data: contactData,
    isLoading: loading,
    error,
    refetch
  } = useQuery<StrapiInfoContactoResponse, Error>({
    queryKey: ['strapi-info-contacto'],
    queryFn: fetchInfoContactoData,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos (gcTime reemplaza a cacheTime en v5)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    contactData: contactData || null,
    seo: mapStrapiSeo(contactData?.data?.SEO) || null,
    loading,
    error: error?.message || null,
    refetch: () => refetch()
  };
};

